import React, { useState, useEffect } from 'react';
import { Award, Target } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23e2e8f0' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='48' fill='%2394a3b8' text-anchor='middle' dy='.3em'%3EPhoto%3C/text%3E%3C/svg%3E";

const TeamMemberCard = ({ member, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
    data-testid={`team-member-${member.id}`}
  >
    <div className="relative h-72 overflow-hidden">
      <img
        src={member.image || placeholderImage}
        alt={member.name}
        className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
    <div className="p-6">
      <h3 className="text-xl font-serif font-bold text-navy mb-1 group-hover:text-forest transition-colors">{member.name}</h3>
      <p className="text-forest font-semibold text-sm font-sans mb-3">{member.position}</p>
      {member.expertise && (
        <div className="flex items-start space-x-2 mb-3">
          <Award className="w-4 h-4 text-navy/40 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-500 italic font-sans">{member.expertise}</p>
        </div>
      )}
      <p className="text-gray-600 text-sm leading-relaxed font-sans">{member.bio}</p>
    </div>
  </motion.div>
);

const Team = () => {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.team);
        setTeamData(response.data);
      } catch (err) {
        console.error('Failed to fetch team data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  if (loading || !teamData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-navy border-t-transparent mx-auto" />
          <p className="mt-4 text-gray-500 font-sans">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>Notre Equipe - Hope for Peace and Development</title>
        <meta name="description" content="Rencontrez l'equipe devouee de HPD: notre Conseil d'Administration et notre equipe de direction qui oeuvrent pour l'autonomisation des femmes et de la communaute entiere et la paix en RDC." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-4 font-sans">Leadership & Gouvernance</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">Notre Equipe</h1>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-lg text-white/70 max-w-2xl font-sans">Des leaders devoues a la transformation sociale</p>
          </motion.div>
        </div>
      </section>

      {/* Board */}
      <section className="py-24" data-testid="board-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center mb-6">
              <Target className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-4 tracking-tight">Conseil d'Administration</h2>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-base md:text-lg text-gray-600 font-sans">Notre Conseil d'Administration guide la vision strategique de HPD et assure la gouvernance exemplaire de l'organisation.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamData.board.map((member, i) => <TeamMemberCard key={member.id} member={member} index={i} />)}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 bg-warmgray" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="w-14 h-14 rounded-xl bg-forest flex items-center justify-center mb-6">
              <Award className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-4 tracking-tight">Equipe de Direction</h2>
            <div className="w-16 h-1 bg-navy rounded-full mb-6" />
            <p className="text-base md:text-lg text-gray-600 font-sans">Notre equipe de direction coordonne les operations quotidiennes et assure la mise en oeuvre de nos programmes sur le terrain.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamData.leadership.map((member, i) => <TeamMemberCard key={member.id} member={member} index={i} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-6 tracking-tight">Ensemble pour un Avenir Meilleur</h2>
          <p className="text-lg text-white/70 mb-10 leading-relaxed font-sans max-w-3xl mx-auto">
            Notre equipe diversifiee et experimentee partage une vision commune: autonomiser les femmes et construire une paix durable en Republique Democratique du Congo.
          </p>
          <a href="/#contact" className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-navy font-semibold hover:bg-gray-100 transition-all shadow-lg font-sans">
            Rejoignez Notre Mission
          </a>
        </div>
      </section>
    </div>
  );
};

export default Team;
